import { useEffect } from 'react';
import { Vector3, Color3, Tools, LinesMesh, MeshBuilder, TransformNode, Scene } from '@babylonjs/core';

// ─── Types ────────────────────────────────────────────────────────────────────

type ArrowsProps = {
  scene: Scene;
  parent: TransformNode;
  visibility: boolean;
};

type RepereProps = {
  scene: Scene;
  visibility: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function createArrows(scene: Scene, parent: TransformNode, visibility: boolean): void {
  const size = 4;
  const shade = 0;

  if (!visibility) return;

  const axes = [
    {
      name: 'red-line',
      color: new Color3(1, shade, shade),
      points: [
        Vector3.Zero(),
        new Vector3(size, 0, 0),
        new Vector3(size * 0.95, 0.05 * size, 0),
        new Vector3(size, 0, 0),
        new Vector3(size * 0.95, -0.05 * size, 0),
      ],
    },
    {
      name: 'green-line',
      color: new Color3(shade, 1, shade),
      points: [
        Vector3.Zero(),
        new Vector3(0, size, 0),
        new Vector3(-0.05 * size, size * 0.95, 0),
        new Vector3(0, size, 0),
        new Vector3(0.05 * size, size * 0.95, 0),
      ],
    },
    {
      name: 'blue-line',
      color: new Color3(shade, shade, 1),
      points: [
        Vector3.Zero(),
        new Vector3(0, 0, size),
        new Vector3(0, -0.05 * size, size * 0.95),
        new Vector3(0, 0, size),
        new Vector3(0, 0.05 * size, size * 0.95),
      ],
    },
  ];

  axes.forEach(({ name, color, points }) => {
    const line = MeshBuilder.CreateLines(name, { points }, scene);
    line.color = color;
    line.parent = parent;
  });
}

function createTransformNode(
  name: string,
  scene: Scene,
  parent: TransformNode,
  options: {
    position?: Vector3;
    rotation?: Vector3;
    scaling?: Vector3;
  } = {}
): TransformNode {
  const node = new TransformNode(name, scene);
  node.parent = parent;
  if (options.position) node.position = options.position;
  if (options.rotation) node.rotation = options.rotation;
  if (options.scaling) node.scaling = options.scaling;
  return node;
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function Repere({ scene, visibility }: RepereProps) {
  useEffect(() => {
    // Nœud racine
    const root = new TransformNode('transform-node', scene);
    root.setEnabled(visibility);

    // Définition de tous les nœuds enfants
    const nodes = [
      {
        name: 'special',
        position: new Vector3(15, 0, -10),
      },
      {
        name: 'hud',
        position: new Vector3(-15, 0, -10),
      },
      {
        name: 'player1',
        position: new Vector3(15, 0.3, 5),
        rotation: new Vector3(0, Tools.ToRadians(-112.5), 0),
      },
      {
        name: 'player2',
        position: new Vector3(0, 0.3, 10),
        rotation: new Vector3(0, Tools.ToRadians(180), 0),
      },
      {
        name: 'player3',
        position: new Vector3(-15, 0.3, 5),
        rotation: new Vector3(0, Tools.ToRadians(112.5), 0),
      },
      {
        name: 'table',
        position: new Vector3(0, 0.1, 0),
        scaling: new Vector3(1.5, 1.5, 1.5),
      },
      {
        name: 'deck',
        position: new Vector3(13, 1, -5),
        rotation: new Vector3(0, Tools.ToRadians(-45), 0),
      },
      {
        name: 'player0',
        position: new Vector3(0, 3, -10),
        scaling: new Vector3(1.5, 1.5, 1.5),
        rotation: new Vector3(Tools.ToRadians(-33), 0, 0),
      },
    ];

    // Créer chaque nœud et ses flèches
    nodes.forEach(({ name, ...options }) => {
      const node = createTransformNode(name, scene, root, options);
      createArrows(scene, node, visibility);
    });

    // Cleanup : détruire tous les nœuds à la destruction du composant
    return () => {
      root.getChildTransformNodes(false).forEach(n => n.dispose());
      scene.getMeshesByTags('arrows')?.forEach(m => m.dispose());
      root.dispose();
    };
  }, [scene, visibility]);

  // Ce composant ne rend rien dans le DOM React
  return null;
}